import React, { useMemo, useCallback, useRef, useEffect, useContext } from 'react';
import { Input, ConfigProvider, Space } from 'antd';
import type { InputProps as AntInputProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey, getValidateStoreKey } from '../../base/storeKeyUtils';
import { parseChangeAction, createCommand } from '../../base/actionParser';
import { processValidationRules, validateWithSchema } from '../../base/validationUtils';
import { ValidateHelp } from '../../base/ValidateHelp';
import type { BaseComponentProps, DataSetConfig, ValidationDisplayConfig } from '../../types/common';
import { DataSetInheritContext, useResolvedDataSet } from '../../base/DataSetInheritContext';
import { deriveScopedComponentId } from '../../base/storePropsUtils';
import { resolveAffixRenderConfig, SPACE_ADDON_NOWRAP_STYLE } from '../../utils/inputAffixUtils';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';
import { useBufferedStoreFieldValue } from '../../base/hooks/useBufferedStoreFieldValue';

// ---------------------------------------------------------------------------
// 类型定义
// ---------------------------------------------------------------------------

export interface InputSemanticStyles {
  root?: React.CSSProperties;
  prefix?: React.CSSProperties;
  input?: React.CSSProperties;
  suffix?: React.CSSProperties;
  count?: React.CSSProperties;
}

export interface InputToken {
  activeBorderColor?: string;
  activeShadow?: string;
  addonBg?: string;
  hoverBorderColor?: string;
  inputFontSize?: number;
  inputFontSizeLG?: number;
  inputFontSizeSM?: number;
  paddingBlock?: number;
  paddingBlockLG?: number;
  paddingBlockSM?: number;
  paddingInline?: number;
  paddingInlineLG?: number;
  paddingInlineSM?: number;
}

export interface InputValidationRule {
  type?: string;
  required?: boolean;
  message?: string;
  pattern?: string | RegExp;
  min?: number;
  max?: number;
}

export interface AndInputProps
  extends Omit<AntInputProps, 'onChange' | 'prefix' | 'suffix' | 'addonBefore' | 'addonAfter'>,
    BaseComponentProps,
    DataSetConfig,
    ValidationDisplayConfig {
  /** Ant Design Icons 组件名，由设计器 icon trait 写入 */
  prefix?: string;
  suffix?: string;
  addonBefore?: string;
  addonAfter?: string;
  /** 迭代器 / Masonry 单元下标，与 AndSelect 一致；仅用于导出与动作占位，不参与 Input DOM */
  index?: number;
  changeAction?: string;
  pressEnterAction?: string;
  clearAction?: string;
  rules?: InputValidationRule[];
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  onChange?: (value: string, e: React.ChangeEvent<HTMLInputElement>) => void;
  styles?: InputSemanticStyles;
  token?: InputToken;
}

// ---------------------------------------------------------------------------
// 常量与纯函数
// ---------------------------------------------------------------------------

const SKIP_PROPS_KEYS = ['value', 'validateStatus', 'help'] as const;
const VALIDATE_DEBOUNCE_MS = 200;

function filterEmptyToken(token: InputToken | undefined): Record<string, unknown> | undefined {
  if (!token) return undefined;
  const result: Record<string, unknown> = {};
  Object.entries(token).forEach(([key, value]) => {
    if (value !== '' && value !== undefined && value !== null) result[key] = value;
  });
  return Object.keys(result).length > 0 ? result : undefined;
}

function mergeBasePropsWithStore(
  base: Record<string, unknown>,
  storeProps: Record<string, unknown> | null,
  options: { develop?: boolean }
): Record<string, unknown> {
  const out = { ...base };
  if (storeProps && typeof storeProps === 'object') {
    Object.keys(storeProps).forEach((key) => {
      if (!SKIP_PROPS_KEYS.includes(key as any) && storeProps[key] !== undefined && storeProps[key] !== null) {
        out[key] = storeProps[key];
      }
    });
  }
  if (options.develop) {
    const isDisabled = out.disabled;
    (out as any).disabled = false;
    (out as any).readOnly = true;
    if (isDisabled) {
      (out as any).className = `${(out.className as string) || ''} ant-input-disabled`.trim();
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// 组件
// ---------------------------------------------------------------------------

type PropsWithExtras = AndInputProps & Record<string, unknown>;

const AndInputInner: React.FC<AndInputProps> = (props) => {
  const {
    id,
    sessionId,
    develop,
    dataSet,
    dataSetField,
    index,
    changeAction,
    onChange,
    value: propValue,
    styles,
    token,
    rules,
    validateOnChange,
    validateOnBlur,
    showValidateHelp = true,
    focusAction: _focusAction,
    blurAction: _blurAction,
    pressEnterAction,
    clearAction,
    bordered: _bordered,
    ...restProps
  } = props as PropsWithExtras;

  const resolved = useResolvedDataSet(dataSet, dataSetField, sessionId);
  const effectiveDataSet = resolved.dataSet;
  const effectiveDataSetField = resolved.dataSetField;
  const effectiveSessionId = resolved.sessionId;
  const ctx = useContext(DataSetInheritContext);
  const effectiveIndex = index ?? ctx?.index;
  const effectiveParentIndex = ctx?.parentIndex;

  const lastChangeValueRef = useRef('');
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const addCommand = useAndStore((s) => s.addCommand);
  const getDataSet = useAndStore((s) => s.getDataSet);
  const setDataSet = useAndStore((s) => s.setDataSet);
  const getProps = useAndStore((s) => s.getProps);
  const setProps = useAndStore((s) => s.setProps);
  const setValidateResult = useAndStore((s) => s.setValidateResult);

  const dataSetKey = useMemo(
    () => getDataSetStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const derivedPropsComponentId = useMemo(
    () => (effectiveSessionId ? deriveScopedComponentId(id, ctx, index) : null),
    [effectiveSessionId, id, ctx?.parentIndex, ctx?.index, index]
  );
  const storeProps = useAndStore((s) =>
    derivedPropsComponentId
      ? s.propsStore[`${effectiveSessionId}_props`]?.[derivedPropsComponentId] ?? null
      : null
  );
  const validateStoreKey = useMemo(
    () => getValidateStoreKey(effectiveSessionId, effectiveDataSet),
    [effectiveSessionId, effectiveDataSet]
  );
  const validateErrorMsg = useAndStore((s) =>
    validateStoreKey && effectiveDataSetField
      ? (s.validateStore[validateStoreKey]?.[effectiveDataSetField] ?? '')
      : ''
  );
  const storeValue = useAndStore((s) =>
    dataSetKey && effectiveDataSetField
      ? s.getDataSet(dataSetKey, effectiveDataSetField)
      : undefined
  );
  /** 监听 validateAction：Extend 通过 updateItem(dataSetKey_validateAction, { validate: ts }) 触发整表校验 */
  const validateActionKey = useMemo(
    () =>
      effectiveDataSet
        ? getDataSetStoreKey(effectiveSessionId, `${effectiveDataSet}_validateAction`)
        : undefined,
    [effectiveSessionId, effectiveDataSet]
  );
  const validateActionTrigger = useAndStore((s) =>
    validateActionKey
      ? (s.dataSets[validateActionKey] as { validate?: number } | undefined)?.validate
      : undefined
  );

  const displayValue = useMemo(() => {
    if (storeValue !== undefined) return storeValue;
    if (storeProps?.value !== undefined) return storeProps.value;
    return propValue;
  }, [storeValue, storeProps?.value, propValue]);

  const flushToStore = useCallback(
    (newValue: string) => {
      if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, newValue);
      }
    },
    [dataSetKey, effectiveDataSetField, setDataSet],
  );

  const useStoreBinding = Boolean(dataSetKey && effectiveDataSetField && !onChange);
  const [draftValue, setDraftValue, flushDraftOnBlur] = useBufferedStoreFieldValue(
    storeValue,
    propValue,
    undefined,
    flushToStore,
    useStoreBinding,
  );

  const inputDisplayValue = useStoreBinding ? draftValue : displayValue;

  const mergedProps = useMemo(
    () => mergeBasePropsWithStore(restProps as Record<string, unknown>, storeProps ?? null, { develop }),
    [restProps, storeProps, develop]
  );

  const {
    prefixIconNode,
    suffixIconNode,
    prefixAddonText,
    suffixAddonText,
    inputMergedProps,
  } = useMemo(() => {
    const m = mergedProps as Record<string, unknown>;
    const prefix = m.prefix;
    const suffix = m.suffix;
    const addonBefore = m.addonBefore;
    const addonAfter = m.addonAfter;
    const {
      prefix: _p,
      suffix: _s,
      addonBefore: _ab,
      addonAfter: _aa,
      ...inputRest
    } = m;
    const prefixConfig = resolveAffixRenderConfig({
      iconName: prefix === undefined || prefix === null ? undefined : String(prefix),
      label: addonBefore === undefined || addonBefore === null ? undefined : String(addonBefore),
    });
    const suffixConfig = resolveAffixRenderConfig({
      iconName: suffix === undefined || suffix === null ? undefined : String(suffix),
      label: addonAfter === undefined || addonAfter === null ? undefined : String(addonAfter),
    });
    return {
      prefixIconNode: prefixConfig.iconNode,
      suffixIconNode: suffixConfig.iconNode,
      prefixAddonText: prefixConfig.iconConfigured ? undefined : prefixConfig.labelText,
      suffixAddonText: suffixConfig.iconConfigured ? undefined : suffixConfig.labelText,
      inputMergedProps: inputRest,
    };
  }, [mergedProps]);

  const inputDomProps = useMemo(() => {
    const merged = {
      ...(inputMergedProps as Record<string, unknown>),
      ...(styles ? { styles } : {}),
    };
    return prepareAntdDomProps(merged);
  }, [inputMergedProps, styles]);

  const applyValidateResult = useCallback(
    (help: string) => {
      const msg = help ?? '';
      if (effectiveSessionId && effectiveDataSet && effectiveDataSetField) {
        setValidateResult(
          effectiveSessionId,
          effectiveDataSet,
          effectiveDataSetField,
          msg,
        );
      }
      if (effectiveSessionId && derivedPropsComponentId) {
        const current = getProps(effectiveSessionId, derivedPropsComponentId) || {};
        setProps(effectiveSessionId, derivedPropsComponentId, {
          ...current,
          validateStatus: msg ? 'error' : undefined,
          help: msg,
        });
      }
    },
    [
      effectiveSessionId,
      effectiveDataSet,
      effectiveDataSetField,
      derivedPropsComponentId,
      setValidateResult,
      getProps,
      setProps,
    ]
  );

  const validate = useCallback(
    (value: string) => {
      const processedRules = processValidationRules(rules || []);
      validateWithSchema(
        processedRules,
        value,
        () => applyValidateResult(''),
        (errs) => applyValidateResult(errs[0]?.message ?? '')
      );
    },
    [rules, applyValidateResult]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      lastChangeValueRef.current = newValue;
      if (onChange) {
        onChange(newValue, e);
        return;
      }
      if (useStoreBinding) {
        setDraftValue(newValue);
      } else if (dataSetKey && effectiveDataSetField) {
        setDataSet(dataSetKey, effectiveDataSetField, newValue);
      }
      if (changeAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(changeAction, newValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
          ...inputMergedProps,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
      if (validateOnChange) {
        if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
        validateTimerRef.current = setTimeout(() => {
          validateTimerRef.current = null;
          validate(newValue);
        }, VALIDATE_DEBOUNCE_MS);
      }
    },
    [
      onChange,
      useStoreBinding,
      setDraftValue,
      dataSetKey,
      effectiveDataSetField,
      setDataSet,
      changeAction,
      effectiveSessionId,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      inputMergedProps,
      addCommand,
      validateOnChange,
      validate,
      ctx,
    ]
  );

  const handleBlur = useCallback(() => {
    if (useStoreBinding) flushDraftOnBlur();
    if (!validateOnBlur) return;
    const valueToValidate = useStoreBinding
      ? draftValue
      : dataSetKey && effectiveDataSetField
        ? String(getDataSet(dataSetKey, effectiveDataSetField) ?? '')
        : lastChangeValueRef.current;
    validate(valueToValidate);
  }, [
    useStoreBinding,
    flushDraftOnBlur,
    validateOnBlur,
    draftValue,
    dataSetKey,
    effectiveDataSetField,
    getDataSet,
    validate,
  ]);

  const handlePressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const merged = inputMergedProps as Record<string, unknown>;
      const userOnPressEnter = merged.onPressEnter as
        | ((ev: React.KeyboardEvent<HTMLInputElement>) => void)
        | undefined;
      if (userOnPressEnter) {
        userOnPressEnter(e);
        return;
      }
      if (pressEnterAction && effectiveSessionId) {
        const { actionName, arg } = parseChangeAction(pressEnterAction, inputDisplayValue, {
          id,
          dataSet: effectiveDataSet,
          dataSetField: effectiveDataSetField,
          index: effectiveIndex,
          parentIndex: effectiveParentIndex,
          ...inputMergedProps,
        }, ctx);
        addCommand(effectiveSessionId, createCommand(actionName, arg));
      }
    },
    [
      inputMergedProps,
      pressEnterAction,
      effectiveSessionId,
      inputDisplayValue,
      id,
      effectiveDataSet,
      effectiveDataSetField,
      effectiveIndex,
      effectiveParentIndex,
      addCommand,
      ctx,
    ]
  );

  const handleClear = useCallback(() => {
    const merged = inputMergedProps as Record<string, unknown>;
    const userOnClear = merged.onClear as (() => void) | undefined;
    if (userOnClear) {
      userOnClear();
      return;
    }
    if (clearAction && effectiveSessionId) {
      const { actionName, arg } = parseChangeAction(clearAction, '', {
        id,
        dataSet: effectiveDataSet,
        dataSetField: effectiveDataSetField,
        index: effectiveIndex,
        parentIndex: effectiveParentIndex,
        ...inputMergedProps,
      }, ctx);
      addCommand(effectiveSessionId, createCommand(actionName, arg));
    }
  }, [
    inputMergedProps,
    clearAction,
    effectiveSessionId,
    id,
    effectiveDataSet,
    effectiveDataSetField,
    effectiveIndex,
    effectiveParentIndex,
    addCommand,
    ctx,
  ]);

  useEffect(
    () => () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (
      validateActionTrigger == null ||
      !dataSetKey ||
      !effectiveDataSetField
    )
      return;
    const value = String(getDataSet(dataSetKey, effectiveDataSetField) ?? '');
    validate(value);
  }, [validateActionTrigger, dataSetKey, effectiveDataSetField, getDataSet, validate]);

  const filteredToken = useMemo(() => filterEmptyToken(token), [token]);
  const themeConfig = useMemo(
    () => (filteredToken ? { components: { Input: filteredToken } } : undefined),
    [filteredToken]
  );

  const help = !develop
    ? (effectiveSessionId && effectiveDataSet && effectiveDataSetField
        ? validateErrorMsg
        : storeProps?.help ?? '')
    : undefined;
  const validateStatus = !develop && help ? 'error' : undefined;

  const inputEl = (
    <>
      <Space.Compact style={{ width: '100%' }}>
        {prefixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{prefixAddonText}</Space.Addon> : null}
        <Input
          {...(inputDomProps as any)}
          prefix={prefixIconNode}
          suffix={suffixIconNode}
          value={inputDisplayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onPressEnter={handlePressEnter}
          onClear={handleClear}
          status={validateStatus}
        />
        {suffixAddonText ? <Space.Addon style={SPACE_ADDON_NOWRAP_STYLE}>{suffixAddonText}</Space.Addon> : null}
      </Space.Compact>
      <ValidateHelp help={help} showValidateHelp={showValidateHelp} />
    </>
  );

  return themeConfig ? <ConfigProvider theme={themeConfig}>{inputEl}</ConfigProvider> : inputEl;
};

AndInputInner.displayName = 'AndInput';

export const AndInput = React.memo(AndInputInner);
export default AndInput;
